import { IconsDropdown, useToasts } from '@brunommsilva/react-foundations';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CATEGORY_ICON_MAP, CATEGORY_ICONS } from '../../config/category-icons';
import useUpdateCategory from '../../hooks/categories/use-update-category';
import { Category } from '../../models/categories';

interface State {
  id?: string;
  name?: string;
  description?: string;
  icon?: string;
}

const INITIAL_STATE: State = {};

interface Props {
  show: boolean;
  category?: Category;
  onEdited: () => void;
  onClose: () => void;
}

function EditCategoryModal({ show, onEdited, onClose, category }: Props) {
  const { t } = useTranslation();
  const { showToast } = useToasts();
  const [state, setState] = useState<State>(INITIAL_STATE);
  const {
    updateCategory,
    isUpdating,
    wasUpdatedWithSuccess,
    wasUpdatedWithoutSuccess,
  } = useUpdateCategory();

  useEffect(() => {
    if (category) {
      setState({
        id: category.id,
        name: category.name,
        description: category.description,
        icon: category.icon,
      });
    }
  }, [category]);

  useEffect(() => {
    if (wasUpdatedWithSuccess) {
      showToast({
        title: t('EDIT_CATEGORY_SUCCESS_TOAST.TITLE'),
        message: t('EDIT_CATEGORY_SUCCESS_TOAST.MESSAGE'),
      });
      onEdited();
    }
    if (wasUpdatedWithoutSuccess) {
      showToast({
        title: t('EDIT_CATEGORY_ERROR_TOAST.TITLE'),
        message: t('EDIT_CATEGORY_ERROR_TOAST.MESSAGE'),
        type: 'error',
      });
    }
  }, [wasUpdatedWithSuccess, wasUpdatedWithoutSuccess]);

  const isFormValid = () => {
    return state.id && state.name;
  };

  const handleEditCategory = () => {
    if (!isFormValid()) {
      return;
    }

    updateCategory({
      id: state.id!,
      name: state.name!,
      description: state.description,
      icon: state.icon,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const renderIconPicker = () => {
    const iconOptions = CATEGORY_ICONS.map((icon) => ({
      id: icon.id,
      label: t(icon.translationKey),
    }));

    return (
      <IconsDropdown
        options={iconOptions}
        iconMap={CATEGORY_ICON_MAP}
        value={state.icon}
        onChange={(iconId) => setState({ ...state, icon: iconId })}
        label={t('CATEGORY_ICON')}
      />
    );
  };

  return (
    <Dialog open={show} onClose={onClose} fullWidth>
      <DialogTitle>{t('EDIT_CATEGORY_MODAL.TITLE')}</DialogTitle>
      <DialogContent dividers>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <FormControl fullWidth>
            <TextField
              label={t('CATEGORY_NAME')}
              value={state.name || ''}
              onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
                setState({ ...state, name: change.target.value });
              }}
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              label={t('CATEGORY_DESCRIPTION')}
              value={state.description || ''}
              onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
                setState({ ...state, description: change.target.value });
              }}
            />
          </FormControl>
          {renderIconPicker()}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isUpdating} color="primary" onClick={handleClose}>
          {t('EDIT_CATEGORY_MODAL.CLOSE')}
        </Button>
        <Button
          disabled={isUpdating || !isFormValid()}
          color="error"
          onClick={handleEditCategory}
        >
          {t('EDIT_CATEGORY_MODAL.CONFIRM')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditCategoryModal;
